import { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import Page from '../components/Page';
import Iconify from '../components/Iconify';
import PlayerDetailDialog from '../sections/players/PlayerDetailDialog';
import PlayerSessionsDialog from '../sections/players/PlayerSessionsDialog';
import PlayerWellnessDialog from '../sections/players/PlayerWellnessDialog';
import PlayerOnboardingDialog from '../sections/players/PlayerOnboardingDialog';
import { adminApi } from 'src/utils/adminApi';
import { formatClutchDate, toDateInputValue } from 'src/utils/formatDate';

function formatWorkloadMetric(value) {
  if (value == null || value === '') {
    return '—';
  }
  const number = Number(value);
  if (Number.isNaN(number)) {
    return String(value);
  }
  return Number.isInteger(number) ? String(number) : number.toFixed(1);
}

function WorkloadMetricLine({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ minWidth: 168 }}>
      <Typography variant="caption" sx={{ color: '#6B7280' }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ color: '#F3F4F6', fontWeight: 600 }}>
        {formatWorkloadMetric(value)}
      </Typography>
    </Stack>
  );
}

const SORT_OPTIONS = [
  { value: 'created_at|desc', label: 'Latest joined' },
  { value: 'created_at|asc', label: 'Oldest joined' },
  { value: 'acwr_ratio|desc', label: 'Highest ACWR ratio' },
  { value: 'acwr_ratio|asc', label: 'Lowest ACWR ratio' },
  { value: 'session_count|desc', label: 'Most sessions' },
  { value: 'session_count|asc', label: 'Fewest sessions' },
  { value: 'latest_session_date|desc', label: 'Latest session first' },
  { value: 'latest_session_date|asc', label: 'Oldest session first' }
];

export default function Players() {
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortKey, setSortKey] = useState('created_at|desc');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ lastPage: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState({ open: false, mode: null, player: null });
  const [sessionsDialog, setSessionsDialog] = useState({
    open: false,
    player: null,
    dateFrom: '',
    dateTo: ''
  });

  const openSessionsDialog = (player, { dateFrom = '', dateTo = '' } = {}) => {
    setSessionsDialog({ open: true, player, dateFrom, dateTo });
  };
  const [wellnessDialog, setWellnessDialog] = useState({ open: false, player: null });
  const [onboardingDialog, setOnboardingDialog] = useState({ open: false, player: null });

  const loadPlayers = useCallback(() => {
    const [sort, direction] = sortKey.split('|');
    setLoading(true);
    setError('');

    adminApi
      .listUsers({
        role: 'player',
        q: search,
        sort,
        direction,
        page
      })
      .then((result) => {
        setRows(result.data?.items || []);
        setPagination(result.data?.pagination || { lastPage: 1, total: 0 });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [page, search, sortKey]);

  useEffect(() => {
    loadPlayers();
  }, [loadPlayers]);

  const openDialog = (mode, player) => setDialog({ open: true, mode, player });
  const closeDialog = () => setDialog({ open: false, mode: null, player: null });

  const applySearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  return (
    <Page title={`Players | ${process.env.REACT_APP_PROJECTNAME}`}>
      <Container maxWidth="xl">
        <Box sx={{ py: 3 }}>
          <Typography variant="h4" sx={{ color: '#F3F4F6', mb: 3 }}>
            Players
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                placeholder="Search by player name or email (press Enter)"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    applySearch();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#9CA3AF' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchInput('');
                        setSearch('');
                        setPage(1);
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Sort</InputLabel>
                <Select
                  label="Sort"
                  value={sortKey}
                  onChange={(e) => {
                    setSortKey(e.target.value);
                    setPage(1);
                  }}
                >
                  {SORT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {error && (
            <Typography sx={{ color: '#F87171', mb: 2 }}>
              {error}
            </Typography>
          )}

          <Card sx={{ bgcolor: '#151921', border: '1px solid #232935' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {['Player', 'Workload & ACWR', 'Sessions logged', 'Status', 'Actions'].map((head) => (
                      <TableCell key={head}>{head}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading && rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No players found.
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar src={row.profileImage} alt={row.name}>
                            {row.name?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography sx={{ color: '#F3F4F6', fontWeight: 600 }}>
                              {row.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                              {row.email}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#3F90DF' }}>
                              {row.subscriptionLabel || '—'}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', color: '#6B7280', mt: 0.5 }}>
                              Joined {formatClutchDate(row.createdAt)}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.35}>
                          <WorkloadMetricLine label="ACWR ratio" value={row.latestWorkloadBalanceRatio} />
                          <WorkloadMetricLine label="Acute fatigue" value={row.acuteSystemStress} />
                          <WorkloadMetricLine label="Chronic fitness" value={row.chronicFoundationEngine} />
                          <WorkloadMetricLine label="Latest daily load" value={row.latestDailyLoad} />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Link
                          component="button"
                          underline="hover"
                          sx={{ color: '#3F90DF', fontWeight: 600, display: 'block' }}
                          onClick={() => openSessionsDialog(row)}
                        >
                          {row.sessionsLoggedCount ?? 0} session
                          {(row.sessionsLoggedCount ?? 0) === 1 ? '' : 's'}
                        </Link>
                        {row.latestSessionDate ? (
                          <Link
                            component="button"
                            underline="hover"
                            sx={{ color: '#3F90DF', fontSize: '0.75rem', mt: 0.5, display: 'block' }}
                            onClick={() => {
                              const day = toDateInputValue(row.latestSessionDate);
                              openSessionsDialog(row, { dateFrom: day, dateTo: day });
                            }}
                          >
                            Latest session · {formatClutchDate(row.latestSessionDate)}
                          </Link>
                        ) : (
                          <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mt: 0.5 }}>
                            Latest session · None
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.isBlocked ? 'Locked' : 'Active'}
                          color={row.isBlocked ? 'error' : 'success'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          <Tooltip title="View insights">
                            <IconButton onClick={() => openDialog('summary', row)}>
                              <Iconify icon="carbon:view" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Morning wellness">
                            <IconButton onClick={() => setWellnessDialog({ open: true, player: row })}>
                              <Iconify icon="mdi:heart-pulse" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Onboarding survey">
                            <IconButton onClick={() => setOnboardingDialog({ open: true, player: row })}>
                              <Iconify icon="mdi:clipboard-text-outline" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={row.isBlocked ? 'Unlock user' : 'Lock user'}>
                            <IconButton onClick={() => openDialog('lock', row)}>
                              <Iconify icon={row.isBlocked ? 'mdi:lock-open-outline' : 'mdi:lock-outline'} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Change password">
                            <IconButton onClick={() => openDialog('password', row)}>
                              <Iconify icon="mdi:form-textbox-password" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete player">
                            <IconButton onClick={() => openDialog('delete', row)}>
                              <Iconify icon="mdi:delete-outline" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                Total: {pagination.total || 0}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Previous
                </Button>
                <Typography sx={{ alignSelf: 'center', color: '#9CA3AF' }}>
                  Page {page} / {pagination.lastPage || 1}
                </Typography>
                <Button
                  disabled={page >= (pagination.lastPage || 1)}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </Stack>
            </Stack>
          </Card>
        </Box>
      </Container>

      <PlayerDetailDialog
        open={dialog.open}
        mode={dialog.mode}
        player={dialog.player}
        onClose={closeDialog}
        onCompleted={loadPlayers}
      />

      <PlayerWellnessDialog
        open={wellnessDialog.open}
        player={wellnessDialog.player}
        onClose={() => setWellnessDialog({ open: false, player: null })}
      />

      <PlayerOnboardingDialog
        open={onboardingDialog.open}
        player={onboardingDialog.player}
        onClose={() => setOnboardingDialog({ open: false, player: null })}
      />

      <PlayerSessionsDialog
        key={
          sessionsDialog.open
            ? `${sessionsDialog.player?.id}-${sessionsDialog.dateFrom}-${sessionsDialog.dateTo}`
            : 'closed'
        }
        open={sessionsDialog.open}
        player={sessionsDialog.player}
        initialDateFrom={sessionsDialog.dateFrom}
        initialDateTo={sessionsDialog.dateTo}
        onClose={() =>
          setSessionsDialog({ open: false, player: null, dateFrom: '', dateTo: '' })
        }
      />
    </Page>
  );
}
